import { conditionMessages } from './condition-messages.js'
import { enumOptionsEn } from './enum-options.js'
import { sectionCatalogEn } from './section-catalog.js'

export const en = {
  visualGameStudio: {
    brand: 'Visual Game Studio',
    localizationUnsupported: 'This historical version does not contain complete compiled data and cannot be localized. Select another version.',
    seal: 'Drama',
    viewport: {
      title: 'Use a larger screen',
      body: 'Visual Game Studio is a desktop authoring tool. Use a screen at least 1024px wide to edit packs.',
    },
    nav: { projects: 'Projects', editor: 'Editor', assets: 'Assets', versions: 'Versions', turnVideos: 'Turn videos', runChain: 'Run chain', localization: 'Languages', help: 'Help', settings: 'Settings' },
    auth: { title: 'Sign in to Studio', subtitle: 'Use your platform account to manage packs, assets, versions, and publishing.', email: 'Email', password: 'Password', login: 'Sign in', logout: 'Sign out', loggingIn: 'Signing in', failed: 'Sign in failed' },
    topbar: { preview: 'Preview', publish: 'Publish', noProject: 'No project selected', account: 'Account', themeLight: 'Light theme', themeDark: 'Dark theme' },
    projects: {
      title: 'My Projects',
      subtitle: 'Manage visual-game drafts, publishing status, and visibility.',
      tags: 'Tags', bulkTags: 'Batch tags', bulkTagsPlaceholder: 'Fantasy, Romance, Mystery', applyToFiltered: 'Apply to {count} filtered', bulkTagsApplied: 'Tags applied to {count} packs',
      newProject: 'New Project',
      search: 'Search projects',
      filterAll: 'All',
      filterDraft: 'Draft',
      filterPublished: 'Published',
      sortUpdated: 'Recently updated',
      sortEpisode: 'Level order',
      levelNumber: 'Level {number}',
      emptyTitle: 'No projects yet',
      emptyBody: 'Create the first story pack and enter structured editing.',
      edit: 'Edit',
      duplicate: 'Duplicate',
      rename: 'Rename',
      visibility: 'Visibility',
      delete: 'Archive/Delete',
      createTitle: 'New Project',
      editTitle: 'Edit Project',
      projectTitle: 'Title',
      seriesId: 'Series ID',
      seriesIdPlaceholder: 'e.g. painted_skin',
      seriesTitle: 'Series title',
      scriptTitle: 'Story title',
      seriesTitlePlaceholder: 'e.g. Painted Skin',
      episodeNo: 'Level number',
      episodeNoPlaceholder: '1',
      seriesHint: 'Leave all three fields empty for a standalone level. Series fields must be completed together and level numbers must be unique within a series.',
      newSeries: 'New story',
      createSeries: 'Create story',
      createSeriesTitle: 'New story',
      createSeriesHint: 'Creating a story also creates its first level. Open the story later to add more levels.',
      firstLevelTitle: 'First level title',
      firstLevelTitlePlaceholder: 'e.g. Enter the temple',
      seriesCreated: 'Story and first level created',
      seriesSection: 'Stories',
      standaloneSection: 'Standalone levels',
      manageLevels: 'Manage levels',
      levelCount: '{count} levels',
      newLevel: 'New level',
      newStandalone: 'New standalone level',
      backToSeries: 'Back to stories',
      seriesLevelsSubtitle: 'Manage the {count} levels in this story in order.',
      belongsToSeries: 'Story',
      searchLevels: 'Search levels in this story',
      searchStandalone: 'Search standalone levels',
      noMatchingLevels: 'No matching levels',
      locale: 'Locale',
      associateLevel: 'Link existing level',
      associateLevelTitle: 'Link standalone level',
      associateLevelHint: 'Move the original standalone level into {series} without creating a copy or changing its content.',
      standaloneLevel: 'Standalone level',
      selectStandaloneLevel: 'Select a standalone level',
      levelAssociated: 'Level linked to story',
      associateLevelFailed: 'Failed to link level',
      private: 'Private',
      public: 'Public',
      unlisted: 'Unlisted',
      save: 'Save',
      cancel: 'Cancel',
      creating: 'Creating',
      saving: 'Saving',
      loading: 'Loading projects',
      loadFailed: 'Failed to load projects',
      createFailed: 'Create failed',
      updateFailed: 'Save failed',
      duplicateFailed: 'Duplicate failed',
      deleteFailed: 'Delete failed',
      created: 'Project created',
      updated: 'Project saved',
      duplicated: 'Project duplicated',
      archived: 'Project archived',
      deleted: 'Project deleted',
      confirmDelete: 'Confirm this action? Published packs or packs with user data are archived; unused drafts are permanently deleted.',
      confirmTextLabel: 'Type {title} to confirm',
      scenes: 'Scenes',
      endings: 'Endings',
      updatedAt: 'Updated',
    },
    status: { draft: 'DRAFT', published: 'PUBLISHED', archived: 'ARCHIVED', unknown: 'UNKNOWN' },
    enumOptions: enumOptionsEn,
    sectionGroups: { basic: 'Basic', state: 'Stats & State', world: 'World', cast: 'Cast & Objects', info: 'Information', gameplay: 'Gameplay', ending: 'Endings', assets: 'Assets' },
    editor: {
      title: 'Scenario Editor',
      subtitle: 'Maintain maps, cast, quests, rules, endings, and media by section.',
      section: 'Section',
      readonly: 'You do not have edit permission for this project',
      unsaved: 'Unsaved changes',
      saved: 'Saved',
      save: 'Save',
      revert: 'Revert',
      savedToast: 'Section saved',
      saveFailed: 'Save failed',
      discardConfirm: 'This section has unsaved changes. Discard them?',
      discardTitle: 'Discard unsaved changes',
      discardBody: 'This section has unsaved changes. Leaving now will not write them to the database.',
      stayEditing: 'Keep editing',
      discardCta: 'Discard changes',
      deleteEntityTitle: 'Delete item',
      deleteEntityBody: 'This item will be removed from the current section and written to the database after saving.',
      deleteEntityBlocked: 'This item is referenced in {count} places. Remove those references before deleting it.',
      deleteEntityConfirmLabel: 'Type {label} to confirm deletion.',
      deleteEntityCta: 'Delete item',
      recentChanges: 'Recent changes',
      recentChangesHint: 'Shows database audit records for this draft.',
      auditLoading: 'Loading changes',
      auditLoadFailed: 'Failed to load changes',
      noRecentChanges: 'No changes yet',
      auditActions: {
        section_update: 'Saved section',
        asset_upload: 'Uploaded asset',
        asset_delete: 'Deleted asset',
        compile: 'Compiled',
        publish: 'Published',
        rollback: 'Rolled back',
      },
      focusedCheck: 'Focused publish check',
      structuredTab: 'Structured',
      jsonTab: 'JSON',
      jsonHelp: 'JSON changes sync back to the structured form after valid parsing.',
      jsonInvalid: 'Invalid JSON',
      addRow: 'Add',
      searchOptions: 'Search options',
      dragRow: 'Drag to reorder',
      duplicateRow: 'Duplicate',
      removeRow: 'Remove',
      emptyRows: 'No rows',
      item: 'Item',
      noId: 'No ID',
      sections: {
        mapLocations: 'Locations',
        mapGraph: 'Location graph',
        npcs: 'NPCs',
        objects: 'Objects',
        clues: 'Clues',
        conclusions: 'Conclusions',
        infoBlocks: 'Info blocks',
        sceneEvents: 'Scene events',
        actionRules: 'Action rules',
        endings: 'Endings',
        stateRegistry: 'State registry',
        players: 'Player characters',
        quests: 'Quests',
        mediaTags: 'Media tags',
        videoReferences: 'Video generation references',
        stageRules: 'Stage rules',
        audioRules: 'Audio rules',
        voiceSettings: 'Voice settings',
        voiceSettingsHelp: 'Set default voices first; each language column can override runtime voices, and empty values inherit the default.',
        audioMoodRules: 'BGM rules',
        statSfxRules: 'Stat SFX',
        stateCgRules: 'State CG rules',
        followupRules: 'Follow-up CG',
      },
      stateWorkspace: {
        initialTitle: 'Opening values',
        initialBody: 'When a new run is created, these values are written into it: global flags, NPC state, quest progress, object state, and starting inventory.',
        registryTitle: 'State registry',
        registryBody: 'This is the reference sheet for state keys. It explains what each key means, who writes it, who reads it, and which story or rule it affects.',
        relationshipTitle: 'How they connect',
        relationshipBody: 'Opening values define what is true at the start. The registry explains what each key means. Registry entries do not replace opening values; they support checks, rule references, and maintenance.',
        openingValues: 'Opening values',
        registeredKeys: 'Registered keys',
        initialHint: 'Keep only values that must exist at the start. Nested NPC and object state is shown as dot paths so it lines up with registry keys.',
        registryHint: 'Register every state key read by action rules, conditions, KP, or agents. Clear sources and impact notes make later debugging easier.',
        registryTableTitle: 'State registry entries',
        unregisteredInitialKeys: 'These opening keys do not have registry notes yet:',
        keyPurpose: 'Purpose',
        noRegistryNote: 'No purpose registered',
        noRegistryHint: 'Add a matching key in the state registry below to show its meaning and runtime impact here.',
      },
      sceneWorkspace: {
        scenesGroup: 'Scenes',
        emptyScenes: 'No scenes yet. Add locations in Map first.',
        noScene: 'Create a scene in Map first.',
        noDescription: 'This scene has no description yet.',
        unlocked: 'Unlocked at start',
        locked: 'Locked',
        sceneObjects: 'Scene object definitions',
        sceneClues: 'Scene clues',
        sceneInfoBlocks: 'Scene info blocks',
        sceneEvents: 'Scene events',
        sceneActionRules: 'Scene rules',
        npcNote: 'Choose the global NPCs that appear in this scene and edit their opening scene state. Full profiles stay in global NPC definitions.',
        objectNote: 'Edit global objects mounted in this scene. Object definitions save to global objects; placement saves to map.locations[].objectIds.',
        clueNote: 'This view filters global clues for the current scene. New clues are automatically assigned to this scene.',
        infoNote: 'This view filters info blocks by NPCs in this scene.',
        eventNote: 'This view filters events by the current scene. Saved events receive this scene as locationId.',
        ruleNote: 'This view filters action rules by the current scene. New rules get a location condition to avoid becoming global rules.',
        tabs: {
          overview: 'Overview',
          basic: 'Basic',
          connections: 'Connections',
          npcs: 'NPCs',
          objects: 'Objects',
          clues: 'Clues',
          infoBlocks: 'Info',
          sceneEvents: 'Events',
          actionRules: 'Rules',
        },
        hints: {
          basic: 'Scene name, description, type, and media.',
          connections: 'Movement links from this scene to other scenes.',
          npcs: 'NPCs present in this scene and their opening state.',
          objects: 'Global objects mounted in this scene, with editable definitions.',
          clues: 'Clues related to this scene, its NPCs, or its objects.',
          infoBlocks: 'Revealable info and hidden facts owned by scene NPCs.',
          sceneEvents: 'Entry, discovery, or custom events assigned to this scene.',
          actionRules: 'Rules related to this scene target or location condition.',
        },
      },
      mediaUsage: {
        title: 'Media usage',
        subtitle: 'Shows where each media asset is bound in the game flow. Saving writes these bindings into structured tables used by the game runtime.',
        missingAsset: 'This tag is missing from the asset manifest',
        opening: 'Home opening CG',
        endingBook: 'Ending book background',
        entryStage: 'Entry stage',
        entryStageIdle: 'Entry idle video',
        defaultStage: 'Default stage',
        defaultStageIdle: 'Default idle video',
        revealedStage: 'Revealed stage',
        revealedStageIdle: 'Revealed idle video',
        defaultBgm: 'Default BGM',
        editInMedia: 'Edit in Media global rules',
        editInMediaStageRules: 'Edit in Media stage rules',
        editInMediaAudioRules: 'Edit in Media audio rules',
        editInMediaStateCgs: 'Edit in Media state CG rules',
        editInMediaFollowups: 'Edit in Media follow-ups',
        editInMap: 'Edit in Map location media',
        editInNpcs: 'Edit in NPC media tag',
        editInPlayers: 'Edit in Player character portrait',
        editInEndings: 'Edit in Ending CG',
      },
      sectionCatalog: sectionCatalogEn,
      fieldGroups: {
        public: 'Public layer',
        secret: 'Secret layer',
        secretBadge: 'Secret · hidden from players',
      },
      fields: {
        id: 'ID',
        name: 'Name',
        type: 'Type',
        state: 'State',
        label: 'Label',
        scope: 'Scope',
        source: 'Source',
        detail: 'Detail',
        summary: 'Summary',
        prerequisiteConclusions: 'Prerequisite conclusions',
        relatedClues: 'Related clues',
        content: 'Content',
        priority: 'Priority',
        target: 'Target',
        targetType: 'Target type',
        mode: 'Mode',
        intent: 'Intent',
        claim: 'Claim',
        claims: 'Semantic claims',
        mediaTag: 'Media tag',
        referenceAssets: 'Reference assets',
        stageMediaTags: 'Stage portraits',
        phaseKey: 'Stage',
        tag: 'Tag',
        file: 'File',
        objectKey: 'Object key',
        mime: 'MIME',
        width: 'Width',
        height: 'Height',
        packId: 'Pack ID',
        title: 'Title',
        tagline: 'Tagline',
        assetManifestId: 'Asset manifest ID',
        videoSceneReferenceTag: 'Scene reference image',
        videoNpcReferenceTag: 'NPC reference image',
        videoPlayerReferenceTag: 'Player reference image',
        coverTag: 'Landscape cover (fallback)',
        portrait_cover_tag: 'Portrait cover (9:16)',
        opening: 'Opening text',
        seoTitle: 'List title',
        seoDescription: 'List summary',
        initialLocation: 'Initial location',
        none: 'None',
        initialPhase: 'Initial phase',
        turnLimit: 'Turn limit',
        openingStrategy: 'Opening strategy',
        defaultActionMode: 'Default action mode',
        statsDefinitions: 'Stat definitions',
        statId: 'ID',
        statName: 'Name',
        statColor: 'Color',
        initialValue: 'Initial',
        description: 'Description',
        statRules: 'Stat rules',
        initialStates: 'Initial states',
        stateKey: 'State key',
        stateValue: 'State value',
        initialFlags: 'Initial flags',
        initialNpcStates: 'Initial NPC states',
        initialQuestStates: 'Initial quest states',
        initialSceneEventStates: 'Initial event states',
        initialObjectStates: 'Initial object states',
        initialInventory: 'Initial inventory',
        primaryNpcId: 'Primary NPC',
        keeperRole: 'Keeper role',
        dynamicStoryEnabled: 'Enable dynamic story',
        singleScene: 'Single-scene mode',
        minDistinctTurns: 'Minimum distinct turns',
        maxActiveBranches: 'Maximum active branches',
        maxGeneratedEndings: 'Generated endings per run',
        trackerExpiry: 'Thread expiry turns',
        dynamicStoryPrinciples: 'Dynamic story principles',
        dynamicStoryForbidden: 'Dynamic story restrictions',
        referenceEndings: 'Reference endings',
        referenceEndingsHint: 'Examples are provided only to the branch designer, not to ordinary KP or NPC prompts.',
        referenceEnding: 'Reference ending',
        requiredConsequences: 'Required consequences',
        phaseRules: 'Phase rules',
        phase: 'Phase',
        principles: 'Principles',
        forbidden: 'Forbidden rules',
        hiddenFacts: 'Hidden facts',
        displayName: 'Display name',
        publicProfile: 'Public profile',
        trueProfile: 'True profile',
        coverRole: 'Cover role',
        coverStory: 'Cover story',
        coverAttitude: 'Cover attitude',
        species: 'Species',
        origin: 'Origin',
        hunger: 'Hunger',
        limitation: 'Limitation',
        danger: 'Danger',
        appearance: 'Appearance',
        publicKnowledge: 'Public knowledge',
        privateKnowledge: 'Private knowledge',
        disclosePolicy: 'Disclose policy',
        boundaries: 'Boundaries',
        mustDo: 'Must do',
        mustNotDo: 'Must not do',
        usable: 'Usable',
        damaged: 'Damaged',
        known: 'Known',
        importance: 'Importance',
        ownerNpc: 'Owner NPC',
        startsUnlocked: 'Starts unlocked',
        unlockWhen: 'Unlock condition',
        speakableAs: 'Speakable lines',
        locationId: 'Location ID',
        once: 'Once',
        triggerWhen: 'Trigger condition',
        whenCondition: 'Match condition',
        requires: 'Requires',
        invalidReason: 'Invalid reason',
        overrideKp: 'Override KP',
        stopProcessing: 'Stop processing',
        targetsAll: 'All targets',
        effectsStats: 'Stat changes',
        effectsFlags: 'Flags to set',
        effectsEvidence: 'Evidence to add',
        locationChange: 'Move to location',
        unlockLocations: 'Unlock locations',
        lockLocations: 'Lock locations',
        addInventory: 'Add inventory',
        removeInventory: 'Remove inventory',
        sceneNarration: 'Scene narration',
        visibleFindings: 'Visible findings',
        discoveredObjects: 'Discovered objects',
        npcReaction: 'NPC reaction policy',
        npcDirectiveNpc: 'Directive NPC',
        npcDirectiveTactic: 'NPC tactic',
        npcDirectiveMood: 'NPC mood',
        mustReactTo: 'Must react to',
        mustHide: 'Must hide',
        possibleEnding: 'Immediate ending',
        reason: 'Reason',
        valueType: 'Value type',
        hardState: 'Hard state',
        writers: 'Writers',
        readers: 'Readers',
        runtimeEffect: 'Runtime effect',
        notes: 'Notes',
        npcIds: 'NPC IDs',
        objectIds: 'Object IDs',
        sceneEventIds: 'Event IDs',
        connections: 'Connections',
        to: 'To',
        locked: 'Locked',
        lockedReason: 'Locked reason',
        defaultCharacterId: 'Default character',
        showInSelection: 'Show in character selection',
        gender: 'Gender',
        age: 'Age',
        archetype: 'Archetype',
        portraitTag: 'Portrait media',
        attributes: 'Attributes',
        skills: 'Skills',
        inventory: 'Inventory',
        history: 'History',
        traits: 'Traits',
        mainQuestId: 'Main quest',
        statusAtStart: 'Start status',
        steps: 'Steps',
        completeWhen: 'Complete condition',
        failWhen: 'Fail condition',
        entryTag: 'Entry tag',
        entryIdleTag: 'Entry idle tag',
        defaultTag: 'Default tag',
        defaultIdleTag: 'Default idle tag',
        revealedTag: 'Revealed tag',
        revealedIdleTag: 'Revealed idle tag',
        openingTag: 'Opening CG tag',
        endingBookTag: 'Ending book tag',
        idleTag: 'Idle video tag',
        mood: 'Mood',
        statKey: 'Stat key',
        triggerType: 'Trigger type',
        statePath: 'State path',
        threshold: 'Threshold',
        oncePerRun: 'Once per run',
        triggerScope: 'Trigger scope',
        sortOrder: 'Sort order',
        defaultBgmTag: 'Default BGM tag',
        defaultBgmMood: 'Default BGM mood',
        voiceRole: 'Role',
        defaultVoice: 'Default voice',
        inheritDefaultVoice: 'Inherit default',
        playerVoiceId: 'Player voice',
        npcVoiceId: 'NPC voice',
        endingVoiceId: 'Ending voice',
      },
    },
    conditions: conditionMessages.en,
    assets: {
      title: 'Assets',
      subtitle: 'Manage portraits, CG, audio, and references.',
      file: 'File',
      search: 'Search tag, MIME, or object key',
      upload: 'Upload asset',
      uploading: 'Uploading',
      uploaded: 'Asset uploaded',
      uploadFailed: 'Upload failed',
      generateImage: 'Generate image',
      generatingImage: 'Generating image',
      imageGenerated: 'Image generated and saved as an asset',
      imageGenerationFailed: 'Image generation failed',
      image_purpose: 'Image purpose',
      image_purpose_asset: 'General asset',
      image_purpose_portrait_cover: 'Portrait cover',
      portrait_cover_hint: 'Generates a 9:16 cover (1080 × 1920). After generation, select it as the portrait cover in the script settings.',
      imagePrompt: 'Image prompt',
      imagePromptHint: 'Seedream will generate one 2K image and save it to this pack.',
      deleted: 'Asset deleted',
      delete: 'Delete asset',
      deleteFailed: 'Delete failed',
      openFile: 'Open file',
      inspect: 'Inspect',
      inspector: 'Asset inspector',
      selectAsset: 'Select an asset to inspect preview, metadata, and references.',
      noPreview: 'No preview available',
      noMetadata: 'No metadata',
      loading: 'Loading assets',
      loadFailed: 'Asset load failed',
      emptyTitle: 'Upload the first portrait or CG',
      empty: 'No assets yet',
      duplicateTag: 'This tag already exists. Use a unique tag.',
      referenceCount: '{count} references',
      references: 'References',
      noReferences: 'No section references this asset yet.',
      confirmDeleteTitle: 'Delete asset',
      confirmDeleteBody: 'This asset will be removed from the current draft.',
      confirmReferencedDeleteBody: 'This asset is referenced in {count} places. Those references will break after deletion.',
      confirmTextLabel: 'Type {tag} to confirm deletion',
      confirmDeleteCta: 'Delete asset',
      coverage: {
        total: 'Total assets',
        referenced: 'Used in story',
        unreferenced: 'Unused',
        unreferencedTags: 'Unused asset tags',
        none: 'Every asset is referenced by a story field.',
      },
      filters: { all: 'All' },
      groups: { cg: 'CG and cutscenes', character: 'Characters and portraits', scene: 'Scenes and references', audio: 'Music and SFX', ui: 'UI assets', reference: 'Reference images', other: 'Other files' },
      types: { image: 'Image', video: 'Video', audio: 'Audio', asset: 'File' },
      meta: { type: 'Type', mime: 'MIME', dimensions: 'Dimensions', objectKey: 'Object key', hash: 'Hash' },
    },
    versions: {
      title: 'Versions',
      subtitle: 'Compile, validate, publish, roll back, and preview.',
      compile: 'Compile draft',
      compiling: 'Compiling',
      compileDone: 'Compile finished',
      compileFailed: 'Compile failed',
      loadChecksFailed: 'Failed to load publish checks',
      publishDone: 'Version published',
      publishFailed: 'Publish failed',
      previewFailed: 'Preview authorization failed',
      rollback: 'Rollback',
      rollbackDone: 'Rolled back',
      rollbackFailed: 'Rollback failed',
      checks: 'Publish checks',
      noChecks: 'No checks for the selected version yet. Compile the draft or select a version.',
      version: 'Version',
      emptyTitle: 'No versions yet',
      empty: 'No versions yet. Compile the draft first.',
      publishBlocked: 'Fix blocking publish checks before publishing.',
      jumpToSection: 'Open section',
      snapshot: 'Compile snapshot',
      openSnapshot: 'View snapshot',
      closeSnapshot: 'Close snapshot',
      noSnapshot: 'This version has no compile snapshot to show.',
      checkStatus: { ok: 'Passed', warning: 'Warning', error: 'Error', empty: 'No checks' },
      checkSummary: {
        ok: 'All checks passed.',
        warning: '{warnings} warning checks need review.',
        error: '{errors} blocking checks must be fixed.',
        empty: 'No checks loaded.',
      },
      confirm: {
        publishTitle: 'Publish version',
        publishBody: 'After publishing, the player app will use this version as the live version.',
        publishCta: 'Publish',
        rollbackTitle: 'Rollback version',
        rollbackBody: 'Rollback switches the live version to the selected version. Existing player data is not deleted.',
        rollbackAcknowledge: 'I understand this switches the live version and want to continue.',
        rollbackCta: 'Rollback',
      },
    },
    localization: {
      title: 'Game content localization',
      description: 'Generate or update each language manually for the selected version. Saving a draft never starts translation.',
      externalNotice: 'Generating sends this version’s story text to the configured translation model and stores the result in the content translation table.',
      sourceSummary: 'This version contains {count} localizable text entries.',
      loading: 'Loading translation status…',
      loadFailed: 'Failed to load translation status',
      generate: 'Generate',
      generateAll: 'Generate all languages ({count})',
      generatingAll: 'Generating all languages…',
      batchProgress: 'Processing language {current} of {total} · {completed} completed',
      generateAllDone: 'Translations generated for {count} languages',
      generateAllFailed: 'Batch translation finished: {completed} succeeded, {failed} failed',
      allAlreadyDone: 'All languages are already complete',
      regenerate: 'Regenerate',
      generating: 'Generating…',
      generateDone: 'Translation generated',
      generateFailed: 'Translation generation failed',
      progressLoadFailed: 'Translation is continuing, but progress is temporarily unavailable',
      progressStatus: { idle: 'Preparing', translating: 'Translating', saving: 'Saving', completed: 'Completed', failed: 'Failed' },
      status: { source: 'Source language', missing: 'Pending', ready: 'Completed' },
      locales: { 'zh-CN': 'Simplified Chinese', en: 'English', ja: 'Japanese', ko: 'Korean', es: 'Spanish', pt: 'Portuguese' },
    },
    translationInventory: { title: 'Content details', all: 'All', pending: 'Needs translation', reused: 'Reused', translated: 'Translated', source: 'Source text', target: 'Target translation', notTranslated: 'Not translated yet', empty: 'No content matches this filter.', reuseFrom: 'Unchanged content will be reused from {version}.', reusableCount: '{count} reusable', pendingCount: '{count} pending' },
    common: { retry: 'Retry', refresh: 'Refresh', open: 'Open', close: 'Close', more: 'More', required: 'Required' },
  },
}
